// Re-exports for Searchkit so app-level code depends only on `@travily/product-ui`
// (keeping Searchkit transitively scoped to this package).
import createSearchkitClient from "@searchkit/instantsearch-client";

export { default as Searchkit, ESTransporter } from "searchkit";
export { createSearchkitClient };

type SearchClient = ReturnType<typeof createSearchkitClient>;

// Searchkit's ESTransporter throws on any non-2xx HTTP response — before the
// body is parsed — with "Elasticsearch request failed with status NNN: ...".
// That message is a searchkit internal, which is why `searchkit` and
// `@searchkit/instantsearch-client` are pinned exactly in this package's
// package.json. On a version bump, re-verify the throw in ESTransporter's
// `performNetworkRequest` still matches this regex, and that the
// instantsearch-client adapter still caches failed responses (the
// `purgeCacheOnFailure` workaround below depends on it).
export function isClientFaultError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const status = Number(/failed with status (\d{3})\b/.exec(error.message)?.[1]);
  return status >= 400 && status < 500;
}

// The instantsearch-client adapter converts thrown transport errors into
// empty results AND caches them permanently; wrap `search` so a failed
// request (reported by `consumeFailure`) purges the cache and the next
// identical search state retries the network instead of replaying `hits: []`.
export function purgeCacheOnFailure(searchClient: SearchClient, consumeFailure: () => boolean): void {
  const baseSearch = searchClient.search.bind(searchClient);
  searchClient.search = async (requests: unknown) => {
    const results = await baseSearch(requests);
    if (consumeFailure()) searchClient.clearCache();
    return results;
  };
}
